import React, { useState } from "react";
import { Box, Chip, Drawer, IconButton, Toolbar } from "@mui/material";
import { Chat as ChatIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import MenuPanel from "./MenuPanel"; // Adjust the import path as needed
import { Outlet } from "react-router-dom";
import { ChatPanel } from "llmasaservice-ui";
import { useLLM } from "llmasaservice-client";
import { useUser } from "../contexts/useUser";
import "../../node_modules/llmasaservice-ui/dist/index.css";
import {
  materialDark,
  materialLight,
} from "react-syntax-highlighter/dist/esm/styles/prism";

const TitleLayout = () => {
  const theme = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { project } = useUser();
  const [testRunning, setTestRunning] = useState(false);
  const [chatInitialPrompt, setChatInitialPrompt] = useState("");

  let url = "https://chat.llmasaservice.io/";
  // if the url is localhost or dev.llmasaservice.io, we are in development mode and we should use the dev endpoint
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    url = "https://chat.llmasaservice.io/dev";
  }

  const { send, idle } = useLLM({ project_id: project?.id ?? "", url: url });

  const toggleDrawer = (open: boolean) => (event: any) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const selfTestClick = async () => {
    try {
      setTestRunning(true);

      const testResults: Record<
        string,
        { service: any; status: string; response: string; error: string }
      > = {};

      for (const service of project?.services?.items ?? []) {
        testResults[service?.id ?? ""] = {
          service,
          status: service?.status ?? "unknown",
          response: "",
          error: "",
        };

        const updateResponse = (result: string) => {
          testResults[service?.id ?? ""].response = result;
        };

        const updateError = (error: string) => {
          testResults[service?.id ?? ""].error = error;
        };

        if (service?.status === "active") {
          await send(
            "1+1=",
            [],
            [],
            false,
            false,
            service.id,
            null,
            new AbortController(),
            updateResponse,
            updateError
          );
        }
      }

      // write a prompt summarizing the results
      let prompt = `Summarize and show the individual LLM vendor and model responses. Group the results into service that have a status of "active" and any other values. 
Working means that the simple 1+1= test has the result of 2 somewhere in the response. Not working means that the result is not 2. If there is an error, it will be shown in the error field. The results are shown below:`;

      for (const [key, value] of Object.entries(testResults)) {
        prompt += `\n\nService: ${value.service.name} (status: ${value.status})\nResponse: ${value.response}\nError: ${value.error}`;
      }

      setChatInitialPrompt(prompt);
      setDrawerOpen(true);
    } finally {
      setTestRunning(false);
    }
  };

  const THEME_KEY = "_llmasaservice_theme";
  const userThemePreference = localStorage.getItem(THEME_KEY) || "dark";

  return (
    <>
      {/* Main layout structure */}
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          width: "100vw",
          overflow: "hidden", // Prevent outer scrolling
        }}
      >
        {/* Fixed left sidebar */}
        <Box
          sx={{
            width: "250px",
            flexShrink: 0,
            height: "100vh",
            overflow: "hidden", // Prevent sidebar scrolling
          }}
        >
          <MenuPanel />
        </Box>

        {/* Main content area with fixed header */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            overflow: "hidden", // Prevent overflow outside content area
          }}
        >
          {/* Fixed top bar */}
          <Box
            sx={{
              height: "50px",
              backgroundColor: theme.palette.background.paper,
              flexShrink: 0, // Prevent shrinking
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
              paddingRight: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              zIndex: 2, // Ensure it stays above content
            }}
          >
            <Chip
              label={testRunning ? "Running self-test..." : "Run self-test"}
              sx={{ cursor: "pointer" }}
              onClick={selfTestClick}
            />
            <IconButton
              edge="end"
              color="inherit"
              aria-label="open drawer"
              onClick={toggleDrawer(true)}
            >
              <ChatIcon />
            </IconButton>
          </Box>

          {/* Scrollable content area */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: "auto", // Allow content to scroll
              padding: 2,
              boxSizing: "border-box",
            }}
          >
            <Outlet />
          </Box>
        </Box>
      </Box>

      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box
          sx={{
            width: 350,
            height: "100vh",
            backgroundColor: theme.palette.background.default,
          }}
          role="presentation"
          //onClick={toggleDrawer(false)}
          //onKeyDown={toggleDrawer(false)}
        >
          <ChatPanel
            project_id={project?.id ?? ""}
            initialPrompt={chatInitialPrompt}
            initialMessage={
              chatInitialPrompt === ""
                ? `Hello, use this chat to test the LLM services defined in this project. Tokens are used from the customer called ${project?.name}.`
                : ""
            }
            theme={userThemePreference === "light" ? "light" : "dark"}
            height="90vh"
            width="100%"
            url={url}
            createConversationOnFirstChat={false}
          />
        </Box>
      </Drawer>
    </>
  );
};

export default TitleLayout;
